import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = '10 Common Mistakes Missionaries Make When Raising Support (and How to Fix Them)';
const DESCRIPTION =
    'The recurring mistakes that quietly stall missionary support raising — vague asks, no follow-up, a network that never grows — and a practical fix for each one.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover10.jpg',
            datePublished: '2026-07-28',
            dateModified: '2026-07-28',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/common-mistakes-missionary-support-raising',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What is the single biggest mistake missionaries make when raising support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Treating the ask as a one-time transaction instead of an invitation into ongoing partnership. Missionaries who frame support as 'join me in this' rather than 'give me this' get more recurring commitments and stronger relationships, because donors are agreeing to a role, not just a payment.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I apologize for asking people for money?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No — apologizing frames the ask as an imposition rather than an invitation, and it makes donors less comfortable saying yes, not more. State the need plainly and let the person decide; confidence in the ask is more persuasive than hedging around it.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How specific should my support letter be about the amount I need?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Specific enough that someone could write a check without asking a follow-up question — a real monthly number, not 'whatever you can give.' Vague asks produce vague, usually smaller, responses because the donor has to do the math you should have done for them.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is it a mistake to only ask people once?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Yes, in most cases. A single ask with no follow-up leaves genuine "yes, eventually" responses to quietly die from busyness rather than an actual no. A simple follow-up plan recovers a meaningful share of the support that a one-and-done approach loses.',
                    },
                },
            ],
        },
    ],
};

export default function CommonMistakesMissionarySupportRaising() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/common-mistakes-missionary-support-raising' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-common-mistakes-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover10.jpg" alt="A missionary reviewing a support-raising letter and donor list at a desk" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most missionaries who struggle to raise full support aren't doing anything dramatically wrong — they're making a handful of small, common mistakes that quietly cap how much they raise and how fast they raise it. None of these are character flaws or signs the calling is wrong. They're fixable habits, and fixing even two or three of them tends to move the needle more than any single "big" tactic.</p>

                    <h2>1. Treating the ask as a transaction instead of an invitation</h2>

                    <p>"Please give to my ministry" reads very differently from "join our team as a monthly partner." The first asks for money; the second offers a role. Donors who see themselves as partners — with a name, a mental picture of the work, and a sense of shared purpose — give more consistently and for longer than donors who were simply asked for a favor once.</p>

                    <h2>2. Being vague about the actual number</h2>

                    <p>"Whatever you can give" sounds humble, but it shifts the math onto the donor, and most people default to a smaller, safer number when they have to guess. Naming a real monthly figure — even a specific "$75/month would fully cover our health insurance" — gives people something concrete to say yes to. See <a href="/resources/how-much-support-does-a-missionary-need">how much support a missionary actually needs</a> for how to land on that number in the first place.</p>

                    <h2>3. Waiting until the timeline is already tight</h2>

                    <p>Support raising takes months, not weeks, and starting late compresses a naturally slow process into a stressful sprint. Missionaries who begin building their donor list and sending letters before they feel "ready" consistently reach full support faster than those who wait for the perfect moment. A structured early start, like the one in <a href="/resources/raise-support-fast-30-60-90-day-plan">the 30/60/90-day plan</a>, removes most of the late-stage panic.</p>

                    <h2>4. Only asking the people closest to you</h2>

                    <p>It's natural to start with immediate family and best friends, but stopping there caps support at whatever that small circle can afford. The bigger gains usually come from extended relationships — former coworkers, college classmates, small group members from years ago — who are often more able and willing to give than people assume. <a href="/resources/how-to-ask-friends-and-family-to-support-your-mission-trip">Asking friends and family well</a> is step one, not the whole strategy.</p>

                    <h2>5. Never following up after the first conversation</h2>

                    <p>A meeting that ends in "let me think about it" is not a no — it's an unfinished conversation. Missionaries who skip the follow-up lose a meaningful share of donors who genuinely intended to give but got busy and never circled back on their own. A simple, scheduled <a href="/resources/follow-up-after-support-raising-appointment">follow-up plan</a> recovers most of that lost support without feeling pushy.</p>

                    <h2>6. Going quiet once someone says yes</h2>

                    <p>Landing the commitment feels like the finish line, but it's actually the start of the relationship that keeps the gift recurring for years instead of months. Donors who never hear from you again after their first gift are the ones most likely to quietly cancel. See <a href="/resources/donor-retention-for-missionaries">donor retention for missionaries</a> for the habits that keep a "yes" from fading into silence.</p>

                    <h2>7. Apologizing for the ask itself</h2>

                    <p>Opening a letter or conversation with "I hate asking for money, but..." signals discomfort that donors pick up on, and it can make the ask feel like an imposition rather than an invitation. Stating the need plainly and confidently — without over-explaining or apologizing — tends to land better precisely because it doesn't ask the donor to manage your discomfort along with their decision. <a href="/resources/is-it-biblical-to-ask-for-support">Is it biblical to ask for support?</a> is worth reading if guilt is the real thing holding you back.</p>

                    <h2>8. Copying someone else's letter word-for-word</h2>

                    <p>Templates are useful for structure, but a letter that reads like it was copied loses the one thing that actually moves people: your specific story, in your own voice. Use a template's shape — opening hook, the need, the ask, the next step — but fill it with your own details, your own field, and your own reasons for going.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What is the single biggest mistake missionaries make when raising support?</strong><br />
                    Treating the ask as a one-time transaction instead of an invitation into ongoing partnership.</p>

                    <p><strong>Should I apologize for asking people for money?</strong><br />
                    No — state the need plainly and let the person decide; confidence in the ask lands better than an apology.</p>

                    <p><strong>How specific should my support letter be about the amount I need?</strong><br />
                    Specific enough that someone could write a check without asking a follow-up question.</p>

                    <p><strong>Is it a mistake to only ask people once?</strong><br />
                    Yes, in most cases — a simple follow-up plan recovers support that a one-and-done ask quietly loses.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                        <li><a href="/resources/raise-support-fast-30-60-90-day-plan">How to Raise Support Fast: A 30/60/90-Day Plan for New Missionaries</a></li>
                        <li><a href="/resources/donor-retention-for-missionaries">Donor Retention for Missionaries: How to Stop Supporters From Quietly Lapsing</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this list after watching the same handful of fixable habits show up again and again in missionaries who felt stuck at the same support level for months.</p>
                    <p><small><em>Last updated: 2026-07-28.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/common-mistakes-missionary-support-raising" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
